/**
 * @fileoverview Client-minted `Idempotency-Key`s that survive a RETRY —
 * framework-free core (IDEM-HTTP-001, HARD-STD-001 P20).
 *
 * Promoted 2026-08-14 from pos-web
 * `features/payments/hooks/useSubmitIdempotencyKey.js` (Q-IDEM-1) when payday
 * payroll actions and purchase guest checkout joined the require-key posture —
 * one home for the minting discipline, consumed by every web app. The React
 * binding lives in `@puffin-core/api-client/react`; the submit-key store
 * (Q-CR-14) lives in `submitKeyStore.js`, which an app's composition root alone
 * reaches.
 *
 * THE WHOLE POINT IS THE LIFETIME OF THE KEY, not its randomness:
 *
 *   - `uuid()` minted per call            → a new key on every attempt. Exactly
 *                                           the defect Q-IDEM-3 fixed on the
 *                                           server, wearing different clothes.
 *   - a key pinned for a component's
 *     lifetime                            → `createIdempotencyKeyMiddleware`
 *                                           answers 409 IDEMPOTENCY_KEY_CONFLICT
 *                                           for the same key with a different
 *                                           body hash, locking the operator out
 *                                           of correcting their own typo.
 *
 * So a key source holds `{signature, key}` and re-mints ONLY when the submitted
 * payload changes:
 *
 *   - retry of one submit (same values) → SAME key → the server (and the
 *     provider behind it) replays the original result instead of creating a
 *     second order / payroll run / Stripe object.
 *   - a genuinely new submit (edited values) → NEW key.
 *
 * A key that changes per attempt is WORSE than no key, because it reads as
 * coverage while deduplicating nothing.
 *
 * ⚠️ Do NOT substitute a per-REQUEST tracing id (`generateRequestId` shapes).
 * A tracing id MUST change on every call; an idempotency key must not. The two
 * look interchangeable and are exact opposites.
 */

import { mintIdempotencyKey } from '@puffin-core/retry-policy';

/**
 * Recursively sort object keys so two payloads that differ only in property
 * order produce one signature. Mirrors the canonical-JSON body hash the
 * server's `createIdempotencyKeyMiddleware` computes, so client and server
 * agree on what "the same request" means.
 *
 * @param {*} value
 * @returns {*} the same value with every plain-object key ordered
 */
export function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const ordered = {};
    for (const key of Object.keys(value).sort()) ordered[key] = canonicalize(value[key]);
    return ordered;
  }
  return value;
}

/**
 * Stable string identity of one submitted payload.
 *
 * @param {*} payload
 * @returns {string} always a string — `JSON.stringify(null)` is `'null'`, so it
 *   can never collide with a source's initial `null` sentinel
 */
export function submitSignature(payload) {
  return JSON.stringify(canonicalize(payload === undefined ? null : payload));
}

/**
 * The ONE minter lives in `@puffin-core/retry-policy` (moved there UNCHANGED, CLIENT-RETRY-IDEMPOTENCY-001), re-exported
 * here identity-pinned for `submitKeyStore.js` and never from the package index (Q-R8PORT3-7). This module and the store
 * are its named holders under `web/no-per-call-idempotency-key`'s mint arm: a submit holds one key across its retries
 * (the source below, or the store), and a poll carries NO key (IDEM-HTTP-001 `exempt(req)`, R7-27).
 */
export { mintIdempotencyKey };

/**
 * Framework-free key source: returns `nextKey(payload)` closing over the held
 * `{signature, key}` pair. The React hook binds one source to a component via
 * `useRef`; non-React callers (services, native) hold the source themselves.
 *
 * @param {string} namespace - Short operation name, prefixed onto every key.
 * @returns {(payload: *) => string}
 */
export function createSubmitIdempotencyKeySource(namespace) {
  let held = { signature: null, key: null };
  return function nextKey(payload) {
    const signature = submitSignature(payload);
    if (held.signature !== signature) {
      held = { signature, key: mintIdempotencyKey(namespace) };
    }
    return held.key;
  };
}
